import { formatCalendarDate } from '../../../utils/calendarDate';
import React, { createContext, useContext, useEffect, useId, useRef, useState } from 'react';
import { AlertTriangle, ArrowDown, ArrowUp, Inbox, Info, Minus, RefreshCw } from 'lucide-react';
import '../analytics.css';
import { SEMANTIC, semanticFor, statusLabel } from '../theme.ts';

/**
 * Small shared building blocks for the analytics dashboards (Feature 5.3,
 * reports #1-#41 across all 5 personas + the self-serve grid). Kept local to
 * `analytics/` rather than reused from `src/admin/` to avoid coupling a
 * tenant-facing surface to the admin app's component tree.
 *
 * Chart color tokens, the semantic status vocabulary, and axis/tooltip
 * styling live in `../theme.ts` — import from there for anything
 * chart-specific (recharts). This file owns the card/KPI/status primitives.
 */

// ---------------------------------------------------------------------------
// Widget frame context — lets `WidgetCard` render "bare" (no outer card, no
// repeated title) when it's nested inside the self-serve grid's own tile
// chrome, without every persona-dashboard usage having to know about it.
// Standalone persona-dashboard usage (the default, no provider) is
// unaffected: WidgetCard renders its full card frame + header there.
// ---------------------------------------------------------------------------
const WidgetFrameContext = createContext<{ embedded: boolean }>({ embedded: false });

export const WidgetFrameProvider: React.FC<{ embedded?: boolean; children: React.ReactNode }> = ({
  embedded = true,
  children,
}) => <WidgetFrameContext.Provider value={{ embedded }}>{children}</WidgetFrameContext.Provider>;

export function useWidgetFrame(): { embedded: boolean } {
  return useContext(WidgetFrameContext);
}

// ---------------------------------------------------------------------------
// Page / section headers
// ---------------------------------------------------------------------------

/** Consistent page header for each persona dashboard: icon, title, one-line description, and an optional right-aligned action / toolbar row. */
export const PageHeader: React.FC<{
  icon: React.ReactNode;
  iconClassName?: string;
  title: string;
  description?: string;
  toolbar?: React.ReactNode;
  actions?: React.ReactNode;
}> = ({ icon, iconClassName = 'bg-blue-50 text-blue-600', title, description, toolbar, actions }) => (
  <div className="mb-8">
    <div className="flex flex-wrap items-start justify-between gap-4">
      <div className="flex items-center gap-3.5 min-w-0">
        <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${iconClassName}`}>
          {icon}
        </div>
        <div className="min-w-0">
          <h1 className="text-xl font-semibold tracking-tight text-slate-900 truncate">{title}</h1>
          {description ? <p className="mt-0.5 text-sm text-slate-500">{description}</p> : null}
        </div>
      </div>
      {actions ? <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div> : null}
    </div>
    {toolbar ? (
      <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-slate-200 pt-4">{toolbar}</div>
    ) : null}
  </div>
);

/** Compact section label used to group widgets on a persona page (replaces ad-hoc `<h2>` per dashboard). */
export const SectionHeader: React.FC<{ title: string; description?: string }> = ({ title, description }) => (
  <div className="mb-2.5">
    <h2 className="text-xs font-semibold uppercase tracking-wide text-slate-500">{title}</h2>
    {description ? <p className="mt-0.5 text-xs text-slate-400">{description}</p> : null}
  </div>
);

// ---------------------------------------------------------------------------
// Methodology info popover (CFO KPI band, feedback round 2026-08)
// ---------------------------------------------------------------------------

/**
 * Small "ⓘ" affordance that reveals a plain-English methodology explainer on
 * hover or click/tap — accessible via keyboard (Enter/Space toggles, Escape
 * closes) and touch (no hover required). Deliberately tiny and inline so it
 * drops next to a `StatCard` title without disrupting the KPI band's layout.
 * Used for the CFO Compliance Score / Coverage / Score Delta / Issue Penalty
 * tiles, but generic enough for any KPI that needs a one-off methodology note.
 */
export const InfoPopover: React.FC<{ title: string; children: React.ReactNode; className?: string }> = ({
  title,
  children,
  className = '',
}) => {
  const uid = useId();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLSpanElement>(null);

  // Click-to-toggle only. Root-cause note (feedback round 2026-08-23): the
  // previous version ALSO opened on `onFocus`, and a real mouse click fires
  // focus before click (mousedown -> focus -> mouseup -> click) — so the
  // click handler's `setOpen(v => !v)` toggled the popover straight back
  // closed a moment after focus had opened it, making a real click appear to
  // do nothing. Keep it simple: click opens/closes; Escape and outside-click
  // close; no hover/focus auto-open to fight with the click toggle.
  useEffect(() => {
    if (!open) return undefined;
    const handlePointerDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open]);

  return (
    <span ref={rootRef} className={`relative inline-flex ${className}`}>
      <button
        type="button"
        aria-describedby={open ? uid : undefined}
        aria-expanded={open}
        aria-label={`${title} — methodology`}
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        className="relative z-10 flex h-4 w-4 shrink-0 items-center justify-center rounded-full text-slate-300 hover:text-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500/40"
      >
        <Info className="h-3.5 w-3.5" aria-hidden />
      </button>
      {open && (
        <div
          id={uid}
          role="tooltip"
          className="analytics-info-popover absolute left-1/2 top-full z-20 mt-2 w-64 -translate-x-1/2 rounded-lg border border-slate-200 bg-white p-3 text-left text-xs leading-snug text-slate-600 shadow-lg"
        >
          <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-400">{title}</p>
          {children}
        </div>
      )}
    </span>
  );
};

// ---------------------------------------------------------------------------
// KPI tiles
// ---------------------------------------------------------------------------

/** Semantic-colored delta/trend chip, e.g. score vs. prior snapshot. `invert` flips good/bad (use for metrics where a decrease is good, like open issue counts). */
export const DeltaChip: React.FC<{
  value: number | null | undefined;
  suffix?: string;
  invert?: boolean;
  digits?: number;
}> = ({ value, suffix = '', invert = false, digits = 1 }) => {
  if (value == null || Number.isNaN(value)) return null;
  // Treat anything under this magnitude as "no real movement" — a delta that
  // rounds to "0.0" at the display precision (or is genuinely a rounding
  // artifact just off zero) must never render with a directional sign/arrow
  // or good/bad color; that reads as a fake improvement/decline next to an
  // unrelated critical badge (feedback round 2026-08-23).
  const isNeutral = Math.abs(value) < 0.05;
  const isPositive = !isNeutral && value > 0;
  const isNegative = !isNeutral && value < 0;
  const good = invert ? isNegative : isPositive;
  const bad = invert ? isPositive : isNegative;
  const key = isNeutral ? 'neutral' : good ? 'good' : bad ? 'critical' : 'neutral';
  const Icon = isNeutral ? Minus : isPositive ? ArrowUp : ArrowDown;
  return (
    <span
      className={`inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[11px] font-semibold ring-1 ring-inset ${SEMANTIC[key].chipClass}`}
    >
      <Icon className="h-3 w-3" aria-hidden />
      {Math.abs(value).toFixed(digits)}
      {suffix}
    </span>
  );
};

/** KPI tile: large tabular-nums value, uppercase label, optional icon + delta chip. */
export const StatCard: React.FC<{
  title: string;
  value: React.ReactNode;
  icon?: React.ReactNode;
  color?: string;
  subtitle?: string;
  /** Optional trend vs. a prior period; renders a semantic-colored `DeltaChip` next to the value. */
  delta?: number | null;
  deltaSuffix?: string;
  /** Decimal places on the delta chip (default 1; use 0 for integer counts). */
  deltaDigits?: number;
  /** Set for metrics where a decrease is the good direction (e.g. open issue counts). */
  deltaInvert?: boolean;
  /** Optional `InfoPopover` (or any small node) rendered inline after the title — used for the CFO methodology explainers. */
  titleInfo?: React.ReactNode;
}> = ({
  title,
  value,
  icon,
  color = 'bg-blue-50 text-blue-600',
  subtitle,
  delta,
  deltaSuffix = '%',
  deltaDigits = 1,
  deltaInvert = false,
  titleInfo,
}) => (
  <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        {/* Root-cause note (feedback round 2026-08-23): this used to be a single
            <p className="flex ... line-clamp-2 ..."> with the info-icon button
            rendered *inside* it. `line-clamp-2` sets `display: -webkit-box`,
            which directly conflicts with the `flex` utility on the same
            element — only one `display` wins, and the icon (an inline-flex
            child) ended up overlapping the first couple of characters of the
            wrapped text instead of following it in normal flow. Fix: the
            title text is its own block-level, unclamped element (labels are
            allowed to wrap fully — no more mid-word clipping), and the info
            button lives in a *separate* flex-wrap row after it, so it can
            never overlap the title at any width. */}
        <div className="flex flex-wrap items-start gap-x-1.5 gap-y-0.5">
          <p
            // Root-cause note (feedback round 2026-08-23, Reviewer dashboard
            // "Review Summary" widget — "REVIEWED" rendering as "REVIEWE"):
            // a single-word title (no space to wrap on) in a tile squeezed
            // narrow by a fixed `grid-cols-N` mini stat grid had nowhere to
            // break, so it overflowed its `min-w-0` container horizontally
            // and rendered underneath the icon box painted after it in DOM
            // order — visually clipping the tail of the word. `break-words`
            // lets the browser break mid-word as a last resort so the title
            // wraps onto a second line instead of overflowing behind the
            // icon, at any tile width.
            className="text-xs font-medium uppercase leading-tight tracking-wide text-slate-500 break-words"
            title={typeof title === 'string' ? title : undefined}
          >
            {title}
          </p>
          {titleInfo}
        </div>
        <div className="mt-1.5 flex flex-wrap items-baseline gap-x-2 gap-y-1">
          <p className="analytics-kpi-value text-2xl font-bold leading-none text-slate-900">{value}</p>
          {delta !== undefined ? <DeltaChip value={delta} suffix={deltaSuffix} digits={deltaDigits} invert={deltaInvert} /> : null}
        </div>
        {subtitle ? <p className="mt-1.5 text-xs text-slate-400">{subtitle}</p> : null}
      </div>
      {icon ? (
        <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${color}`}>{icon}</div>
      ) : null}
    </div>
  </div>
);

// ---------------------------------------------------------------------------
// Per-widget "last refreshed" + manual refresh (Feature 5.3 polish pass).
// One small helper reused by every widget so the affordance stays visually
// consistent across all 5 persona dashboards and the self-serve grid.
// ---------------------------------------------------------------------------

/** `Updated h:mm:ss` label for a React Query `dataUpdatedAt` epoch-ms timestamp. */
export function formatUpdatedAt(ms: number | null | undefined): string {
  if (!ms) return '';
  const d = new Date(ms);
  return `Updated ${d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit', second: '2-digit' })}`;
}

/**
 * Small "Updated h:mm:ss" timestamp + refresh icon button, wired to a React
 * Query result's `dataUpdatedAt` / `refetch` / `isFetching`. Renders nothing
 * when neither `updatedAt` nor `onRefresh` is supplied, so it's a no-op to
 * drop into any widget header. The icon spins while a refetch is in flight.
 */
export const RefreshMeta: React.FC<{
  updatedAt?: number | null;
  onRefresh?: () => void;
  isFetching?: boolean;
  className?: string;
}> = ({ updatedAt, onRefresh, isFetching = false, className = '' }) => {
  if (updatedAt == null && !onRefresh) return null;
  return (
    <div className={`flex shrink-0 items-center gap-1.5 ${className}`}>
      {updatedAt != null ? (
        <span className="whitespace-nowrap text-[10.5px] tabular-nums text-slate-400" title={new Date(updatedAt).toLocaleString()}>
          {formatUpdatedAt(updatedAt)}
        </span>
      ) : null}
      {onRefresh ? (
        <button
          type="button"
          onClick={onRefresh}
          disabled={isFetching}
          title="Refresh this widget"
          aria-label="Refresh this widget"
          className="flex h-5 w-5 shrink-0 items-center justify-center rounded text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600 disabled:opacity-60"
        >
          <RefreshCw className={`h-3 w-3 ${isFetching ? 'animate-spin' : ''}`} aria-hidden />
        </button>
      ) : null}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Widget card frame (loading / error / empty states)
// ---------------------------------------------------------------------------

const WidgetSkeleton: React.FC = () => (
  <div className="flex-1 space-y-3 py-0.5" aria-hidden="true">
    <div className="analytics-skeleton h-4 w-2/5" />
    <div className="analytics-skeleton h-32 w-full" />
    <div className="flex gap-2">
      <div className="analytics-skeleton h-3 flex-1" />
      <div className="analytics-skeleton h-3 flex-1" />
      <div className="analytics-skeleton h-3 w-1/4" />
    </div>
  </div>
);

/** Card frame with a title, and shared loading / error / empty states for widgets that don't use AppDataTable.
 *  Renders "bare" (no outer card / no repeated title) when nested inside a `WidgetFrameProvider embedded`
 *  self-serve grid tile, whose own tile chrome already supplies the card + title. */
export const WidgetCard: React.FC<{
  title: string;
  description?: string;
  loading?: boolean;
  error?: unknown;
  errorMessage?: string;
  isEmpty?: boolean;
  emptyMessage?: string;
  action?: React.ReactNode;
  /** React Query `dataUpdatedAt` (ms epoch) — renders an "Updated h:mm:ss" label when present. */
  updatedAt?: number | null;
  /** Usually the query's `refetch` — renders a refresh icon button when present. */
  onRefresh?: () => void;
  /** React Query `isFetching` — spins the refresh icon while a refetch is in flight. */
  isFetching?: boolean;
  children: React.ReactNode;
}> = ({
  title,
  description,
  loading,
  error,
  errorMessage = 'Failed to load this report.',
  isEmpty,
  emptyMessage = 'No data available yet.',
  action,
  updatedAt,
  onRefresh,
  isFetching,
  children,
}) => {
  const { embedded } = useContext(WidgetFrameContext);
  const refreshMeta = <RefreshMeta updatedAt={updatedAt} onRefresh={onRefresh} isFetching={isFetching} />;
  const hasRefreshMeta = updatedAt != null || !!onRefresh;
  const headerAction =
    hasRefreshMeta || action ? (
      <div className="flex shrink-0 items-center gap-2">
        {hasRefreshMeta ? refreshMeta : null}
        {action}
      </div>
    ) : null;

  const body = loading ? (
    <WidgetSkeleton />
  ) : error ? (
    <div className="flex flex-1 flex-col items-center justify-center py-10 text-center">
      <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-rose-50">
        <AlertTriangle className="h-5 w-5 text-rose-500" aria-hidden />
      </div>
      <p className="max-w-xs text-sm font-medium text-rose-600">{errorMessage}</p>
    </div>
  ) : isEmpty ? (
    <div className="flex flex-1 flex-col items-center justify-center py-10 text-center">
      <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-slate-100">
        <Inbox className="h-5 w-5 text-slate-400" aria-hidden />
      </div>
      <p className="max-w-xs text-sm text-slate-500">{emptyMessage}</p>
    </div>
  ) : (
    children
  );

  if (embedded) {
    return (
      <div className="flex h-full min-h-0 flex-1 flex-col">
        {description || headerAction ? (
          <div className="mb-2 flex shrink-0 items-start justify-between gap-2">
            {description ? <p className="text-[11px] leading-snug text-slate-400">{description}</p> : <span />}
            {headerAction}
          </div>
        ) : null}
        <div className="flex min-h-0 flex-1 flex-col">{body}</div>
      </div>
    );
  }

  return (
    <div className="flex min-h-[240px] flex-col rounded-xl border border-slate-200 bg-white p-5 shadow-sm transition-shadow duration-150 hover:shadow-md">
      <div className="mb-4 flex shrink-0 items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="text-sm font-semibold text-slate-900">{title}</h3>
          {description ? <p className="mt-0.5 text-xs text-slate-500">{description}</p> : null}
        </div>
        {headerAction}
      </div>
      <div className="flex min-h-0 flex-1 flex-col">{body}</div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Status / progress primitives
// ---------------------------------------------------------------------------

/** Colour-coded status/conclusion chip; unknown or null statuses render neutral gray. Color keyed off the shared semantic vocabulary in `../theme.ts`. */
export const StatusChip: React.FC<{ value: string | null | undefined; fallback?: string }> = ({
  value,
  fallback = 'Not started',
}) => {
  if (!value) {
    return (
      <span className="inline-flex items-center rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-medium text-slate-500 ring-1 ring-inset ring-slate-500/10">
        {fallback}
      </span>
    );
  }
  const key = semanticFor(value);
  const label = statusLabel(value);
  return (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium capitalize ring-1 ring-inset ${SEMANTIC[key].chipClass}`}
    >
      {label}
    </span>
  );
};

/** Simple horizontal progress bar, e.g. confidence score / coverage %. */
export const ProgressBar: React.FC<{ pct: number; colorClass?: string }> = ({ pct, colorClass = 'bg-blue-500' }) => {
  const clamped = Math.max(0, Math.min(100, pct));
  return (
    <div className="h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
      <div className={`h-full rounded-full transition-[width] duration-300 ${colorClass}`} style={{ width: `${clamped}%` }} />
    </div>
  );
};

export function formatPercent(value: number | null | undefined, digits = 0): string {
  if (value == null || Number.isNaN(value)) return '—';
  return `${value.toFixed(digits)}%`;
}

export function formatNumber(value: number | null | undefined): string {
  if (value == null || Number.isNaN(value)) return '—';
  return value.toLocaleString();
}

export function formatDate(value: string | null | undefined): string {
  if (!value) return '—';
  // Calendar dates (test periods) print on the day they were written, in any time zone.
  return formatCalendarDate(value, undefined, {}) ?? '—';
}
